import type { Conversation, Guest, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { conversationTarget } from "../../lib/bookmarks.ts";
import { countOf, type ThreadRef, type Topic } from "../../lib/conversation.ts";
import { outsideOf, spaceUrl } from "../../lib/paths.ts";
import type { Resource } from "../../lib/resource.ts";
import { BookmarkButton } from "../../ui/BookmarkButton.tsx";
import { Link } from "../../ui/Button.tsx";
import { SeverityLabel } from "../../ui/counts.tsx";
import { LINK } from "../../ui/styles.ts";
import { InlineThread, Thread } from "./thread.tsx";

/** Above a conversation: bookmarking it (for you alone). */
function BookmarkBar({ space, topic }: { space: SpaceOut; topic: Topic }) {
  return (
    <div class="-mb-2 flex justify-end">
      <BookmarkButton space={space} target={conversationTarget(topic)} text={t("bookmarks.conversation")} />
    </div>
  );
}

/**
 * The Conversation tab: the item's own thread, then each of its reviews' and findings' threads that has comments
 * (with what it's about, linking the review).
 */
export function ConversationTab({
  space,
  item,
  conv,
  guests = [],
}: {
  space: SpaceOut;
  item: string;
  conv: Resource<Conversation>;
  /** The item's guests (mentionable too). */
  guests?: readonly Guest[];
}) {
  const others = (conv.data?.threads ?? []).filter((x) => x.review);
  return (
    <div class="flex max-w-3xl flex-col gap-8">
      <BookmarkBar space={space} topic={{ item }} />
      <Thread
        space={space}
        topic={{ item }}
        conv={conv}
        at={{}}
        empty={t("comments.none")}
        guests={guests}
        pinReports={true}
      />
      {others.map((x) => {
        const at = { review: x.review ?? undefined, finding: x.finding ?? undefined };
        return (
          <section key={`${x.review}:${x.finding ?? ""}`} class="flex flex-col gap-3">
            <h3 class="flex flex-wrap items-center gap-2 text-sm text-zinc-600">
              {x.finding ? (
                <>
                  {x.severity ? <SeverityLabel severity={x.severity} /> : null}
                  <span class="font-medium text-zinc-800">{x.about ?? t("comments.aFinding")}</span>
                </>
              ) : (
                <span class="font-medium text-zinc-800">
                  {t("comments.onReview", { name: x.about ?? t("comments.someone") })}
                </span>
              )}
              {outsideOf(space) ? null : (
                <Link href={spaceUrl(space, `/reviews/${x.review}`)} class={`${LINK} text-xs`}>
                  {t("comments.seeReview")}
                </Link>
              )}
            </h3>
            <Thread space={space} topic={{ item }} conv={conv} at={at} />
          </section>
        );
      })}
    </div>
  );
}

/** A Conversation tab's name: with how many comments, and a dot when some are new to you. */
export function ConversationLabel({ conv, at }: { conv: Resource<Conversation>; at?: ThreadRef }) {
  // All its threads; with `at`, that one's (a review's, in its item's conversation).
  const all = conv.data?.threads ?? [];
  const one = at ? countOf(all, at) : undefined;
  const threads = at ? (one ? [one] : []) : all;
  const total = threads.reduce((n, x) => n + x.total, 0);
  const unread = threads.reduce((n, x) => n + x.unread, 0);
  return (
    <span class="inline-flex items-center gap-1.5">
      {t("item.tab.conversation")}
      {total > 0 ? <span class="text-xs text-zinc-500 tabular-nums">{total}</span> : null}
      {unread > 0 ? (
        <span class="size-1.5 rounded-full bg-blue-600" title={t("comments.unread", { count: unread })} />
      ) : null}
    </span>
  );
}

/** A rule's conversation, or the space's channel: one thread. */
export function SimpleConversation({
  space,
  topic,
  conv,
}: {
  space: SpaceOut;
  topic: Topic;
  conv: Resource<Conversation>;
}) {
  return (
    <div class="flex max-w-3xl flex-col gap-8">
      <BookmarkBar space={space} topic={topic} />
      <Thread space={space} topic={topic} conv={conv} at={{}} empty={t("comments.none")} />
    </div>
  );
}

/** One of an entity's fields in its conversation: its name, and its thread opening in place. */
function FieldThread({
  space,
  entity,
  field,
  conv,
}: {
  space: SpaceOut;
  entity: string;
  field: { key: string; label?: string | null };
  conv: Resource<Conversation>;
}) {
  return (
    <InlineThread space={space} topic={{ entity }} conv={conv} at={{ field: field.key }}>
      {(toggle, thread) => (
        <li class="flex flex-col gap-2 py-2">
          <span class="flex items-center gap-3 text-sm">
            <span class="font-medium text-zinc-800">{field.label || field.key}</span>
            <code class="text-xs text-zinc-500">{field.key}</code>
            <span class="ml-auto">{toggle}</span>
          </span>
          {thread}
        </li>
      )}
    </InlineThread>
  );
}

/** An entity's conversation: about it in general, then about each of its fields. */
export function EntityConversation({
  space,
  entity,
  conv,
}: {
  space: SpaceOut;
  entity: { id: string; fields?: readonly { key: string; label?: string | null }[] | null };
  conv: Resource<Conversation>;
}) {
  return (
    <div class="flex max-w-3xl flex-col gap-8">
      <BookmarkBar space={space} topic={{ entity: entity.id }} />
      <Thread space={space} topic={{ entity: entity.id }} conv={conv} at={{}} empty={t("comments.none")} />
      {(entity.fields ?? []).length > 0 ? (
        <section>
          <h2 class="mb-1 text-sm font-semibold text-zinc-700">{t("comments.aboutFields")}</h2>
          <ul class="divide-y divide-zinc-100">
            {(entity.fields ?? []).map((f) => (
              <FieldThread key={f.key} space={space} entity={entity.id} field={f} conv={conv} />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
